import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useTranslations } from 'hooks/useTranslations';
import { EPermission } from 'models/permissions';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { usePermission } from 'hooks/usePermission';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { EArtAncillaryTypeName } from 'constants/ArtAccessoryType';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { StyledInfo } from 'pages/Firearms/RegisteredFirearmTableCard/styles';

interface IAmmunitionCard {
  data: ITableDataSource;
  handleEdit?: (item: ITableDataSource) => void;
  hasCustomFields: boolean;
  handleView?: (args: string) => void;
  renderColor: (data: ITableDataSource) => {
    border: string;
    stateField: EChipsVariants;
    background: string;
  };
  isAnyLicenceAuthorityValid: (licenceAuthorityIds: string[]) => boolean;
}

export enum EAmmunitionTableLabels {
  fireCycleId = 'ammunitionTable.accordion.fireCycleId.column',
  lotNumber = 'ammunitionTable.accordion.lotNumber.column',
  batchNumber = 'ammunitionTable.accordion.batchNumber.column',
  asset = 'ammunitionTable.accordion.assetId.column',
  exhibitNumber = 'ammunitionTable.accordion.exhibitNumber.column',
  ammunitionType = 'ammunitionTable.accordion.ammunitionType.column',
  calibre = 'ammunitionTable.accordion.calibre.column',
  ammunitionManufacturer = 'ammunitionTable.accordion.ammunitionManufacturer.column',
  productName = 'ammunitionTable.accordion.productName.column',
  owner = 'ammunitionTable.accordion.owner.column',
  keeper = 'ammunitionTable.accordion.keeper.column',
  state = 'ammunitionTable.accordion.state.column',
}

const AmmunitionCard = ({
  data,
  handleEdit,
  hasCustomFields,
  handleView,
  renderColor,
  isAnyLicenceAuthorityValid,
}: IAmmunitionCard) => {
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();
  const navigate = useNavigate();

  const handleShowOnMap = () => {
    navigate(ERouteLinks.RegisteredAncillariesMap, {
      state: {
        ancillaryUniqueId: data?.id,
        ancillaryType: EArtAncillaryTypeName.AMMUNITION.toLowerCase(),
        legalityUniqueId: data?.legalityUniqueId,
        navigateBack: `${ERouteLinks.Ancillaries}?expand=ammunition`,
        showAdvanced: true,
        showLegalityBar: false,
        breadcrumbs: [
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.ammunition.name'),
            route: `${ERouteLinks.Ancillaries}?expand=ammunition`,
          },
          t('ancillaries.ancillariesLocation.showOnMap.label'),
        ],
      },
    });
  };

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${renderColor(data).border}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: renderColor(data).background,
      }}
    >
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.fireCycleId)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.fireCycleId)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.lotNumber)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.lotNumber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.batchNumber)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.batchNumber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.asset)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.assetId)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.exhibitNumber)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.exhibitNumber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.ammunitionType)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.ammunitionType)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.calibre)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.calibre)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.ammunitionManufacturer)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.manufacturer)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.productName)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.ammunitionProductName)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.owner)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.ownerName)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.keeper)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.keeperName)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTableLabels.state)}
        </Typography>
        {data.state ? <Chips text={data.state} variant={renderColor(data).stateField} /> : '-'}
      </Grid>

      {data.showInactiveOwnerOrKeeperWarning && (
        <Grid sx={{ p: 2 }}>
          <Grid sx={{ alignItems: 'flex-start', gap: 1, display: 'flex' }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('registeredFirearms.accordion.info.column')}
            </Typography>
            <Icon type={EIconTypes.infoWarning} fontSize={20} />
          </Grid>
          <StyledInfo size={{ xs: 12 }}>
            <span style={{ marginLeft: '10px' }}>
              {t('ancillaries.showInactiveOwnerOrKeeperWarning.text')}
            </span>
          </StyledInfo>
        </Grid>
      )}

      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          p: 2,
        }}
      >
        <Grid size={{ xs: 12, sm: 3 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('general.actions.button')}:{' '}
          </Typography>
        </Grid>

        <Grid container spacing={1} sx={{ justifyContent: 'flex-end' }}>
          {handleEdit &&
            hasCustomFields &&
            hasPermissions([EPermission.EDIT_ANCILLARY]) &&
            isAnyLicenceAuthorityValid(data.licenceAuthorityIds) && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  label={t('ammunitionTable.accordion.edit.button')}
                  variant={EButtonVariants.outlined}
                  permission={[EPermission.EDIT_ANCILLARY]}
                  onClick={() => handleEdit(data)}
                  size={EButtonSizes.small}
                  fullWidth
                />
              </Grid>
            )}
          {handleView && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                label={t('ammunitionTable.accordion.details.button')}
                variant={EButtonVariants.outlined}
                onClick={() => handleView(data.id as string)}
                size={EButtonSizes.small}
                fullWidth
              />
            </Grid>
          )}
          {data.isLatLong && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                label={t('registeredFirearms.firearmsDetails.showOnMap.button')}
                variant={EButtonVariants.contained}
                onClick={handleShowOnMap}
                size={EButtonSizes.small}
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export { AmmunitionCard };
